import type {
    AddFileFormDocumentRoutes,
    AvailableWireDoc,
    Document,
    DocumentUploadConfig,
    File,
    User,
} from '@/components/Views/Company/File/file.types';
import type { FormattedMoneyString } from '@/shared/utils/money';
import type { Branch, Client, Company, TpsIntegration, UUID } from '@/types/closinglock.types';
import type { ServerErrorResponse } from './add-file-form-errors.types';

export type DefaultRole = 'buyer';

export type AddFileSubmitClient = Required<Pick<Client, 'first' | 'last' | 'email' | 'phone'>> & {
    id: AddFileClientKey;
    // Optional: the validate request also carries started-but-without-a-role clients so the submit-time
    // role guard can flag them (they are never sent — the guard blocks first).
    role?: Client['role'];
    included: boolean;
};

export interface AddFileRouteTypes {
    validate: string;
    submit: string;
}

/** @todo [CLENG-4993] cleanup when new_add_file_submission_enabled FF is decommissioned */
export interface AddFileRoutes {
    // company portal
    validateRoute: AddFileRouteTypes['validate'];
    submitRoute: AddFileRouteTypes['submit'];
    // tps
    loadTpsOrderRoute: string | null;
    vendorValidateRoute: AddFileRouteTypes['validate'];
    vendorSubmitRoute: AddFileRouteTypes['submit'];
    // new file submission API routes (behind the new_add_file_submission_enabled flag)
    newFileValidateRoute: AddFileRouteTypes['validate'];
    newFileSubmitRoute: AddFileRouteTypes['submit'];
    viewOrderUrl: string | null; // e.g. http://localhost:8000/user/softpro/order/view/abc123
    documentRoutes: {
        esignEditRoute: AddFileFormDocumentRoutes['esign_edit'];
        esignEditVendorRoute: AddFileFormDocumentRoutes['esign_edit'];
    };
}

export type AddFileClient = Required<Omit<Client, 'role'>> & {
    identity_verified?: boolean;
    role?: Client['role'];
};

/** Everything needed to resolve a single client's tasks and whether they belong on the file. */
export interface ClientTasksParams {
    clientOption: AddFileClient;
    documentConfigs: Map<DocumentUploadConfig['documentId'], DocumentUploadConfig>;
    requestWireInfoClientIds: AddFileClient['id'][];
    requestIdvClientIds: AddFileClient['id'][];
    wireDocRecipientIds: AddFileClient['id'][];
}

/** The tasks a client is assigned, plus whether any of them means they belong on the file. */
export interface ClientTasksResolution {
    requestWireInstructions: boolean;
    requestIdentityVerification: boolean;
    isDocumentRecipient: boolean;
    included: boolean;
}

export interface ToConfirmationClientParams extends ClientTasksParams {
    wireDoc: AvailableWireDoc | undefined;
}

export interface ToSubmitClientsParams extends Omit<ClientTasksParams, 'clientOption'> {
    availableClientOptions: AddFileClient[];
}

export type ClientInfoFormRowIndex = string; // Form-row index as string (e.g. "0", "1", "2", …)

/**
 * if new_add_file_submission_enabled is true, identify clients by UUID; legacy submission identifies clients by form row index
 * @todo [CLENG-4993] cleanup when new_add_file_submission_enabled FF is decommissioned
 */
export type AddFileClientKey = ClientInfoFormRowIndex | UUID;

export type EditAvailableClientValues = Pick<AddFileClient, 'first' | 'last' | 'email' | 'phone' | 'role'>;

export interface ClientInputs {
    emails: { name: string; value: string }[];
    phones: { name: string; value: string }[];
    roles: { name: string; value: AddFileClient['role'] }[];
    firsts: { name: string; value: string }[];
    lasts: { name: string; value: string }[];
}

export interface TPSDocument {
    id: string;
    disabled?: boolean;
    label: string;
    name: string;
    source: string;
    value: string;
}

export interface TPSOrderInfo {
    id: number;
    address: string;
    amount_down: string | null;
    autoSelectDocuments: boolean;
    balance_due: string | null;
    branch_id: number;
    clients: AddFileClient[];
    close_date: string;
    created_at: string;
    deleted_at: string | null;
    documents: TPSDocument[];
    earnest_money: string | null;
    force_wire_doc_selection_true: boolean;
    hideRecvWire: boolean;
    hideSendDocs: boolean;
    hideSendWire: boolean;
    notes?: string;
    notified_at: string | null;
    owned_by_user_id: number;
    ref_number: string;
    sales_price: string;
    street_view_url: string | null;
    title_id: number;
    transaction_type: string | null;
    updated_at: string;
    uploaded_at: string | null;
    vendor_order_id: string;
    vendor_transaction_id: string;
    vendor: TpsIntegration;
    wire_doc_id: number;
}

/** @todo [CLENG-1199] remove this */
export interface AddFileGlobalFunctions {
    removeClient: (that: HTMLElement) => void;
}

export interface FileFormScroller {
    scrollToFileDetailsSection: () => void;
    scrollToClientSection: () => void;
    scrollToDocumentSection: () => void;
    scrollToErrorSection: () => void;
}

export enum AddFileFormSectionIds {
    ERROR_BANNER = 'file-error-banner',
    FILE_ADDRESS = 'file-address-section',
    FILE_DETAILS = 'file-details-section',
    FILE_NOTIFICATION = 'file-notification-section',
    CLIENTS = 'clients-section',
    SEND_WIRE_INSTRUCTIONS = 'send-wire-instructions-section',
    DOCUMENTS = 'document-upload-section',
    REQUEST_WIRE_INFORMATION = 'request-wire-information-section',
    REQUEST_IDENTITY_VERIFICATION = 'request-identity-verification-section',
    REQUEST_MORTGAGE_INFORMATION = 'request-mortgage-information-section',
}

/**
 * Legacy request types for POST user/home
 * @todo [CLENG-4993] remove when new_add_file_submission_enabled FF is decommissioned
 */
export interface AddFileSubmitRequestVendorFields {
    vendor: TPSOrderInfo['vendor'];
    vendor_order_id: TPSOrderInfo['vendor_order_id'];
    vendor_transaction_id: TPSOrderInfo['vendor_transaction_id'];
    amount_down: TPSOrderInfo['amount_down'];
    earnest_money: TPSOrderInfo['earnest_money'];
    sales_price: TPSOrderInfo['sales_price'];
}

export interface AddFileSubmitRequest {
    token: string;
    refNumber?: File['ref_number'];
    address?: File['address'];
    companyId?: Company['id'];
    branchId?: Branch['id'];
    closeDate?: File['close_date']; // MM/DD/YYYY
    balanceDue?: FormattedMoneyString;
    notes?: File['notes'];
    wireDocId?: AvailableWireDoc['id'];
    notify?: boolean;
    clients?: AddFileSubmitClient[];
    wireDocRecipientIds: AddFileSubmitClient['id'][];
    requestBankInfoRecipientIds: AddFileSubmitClient['id'][];
    requestIdvRecipientIds: AddFileSubmitClient['id'][];
    notificationUserIds: User['id'][];
    vendor?: AddFileSubmitRequestVendorFields;
    documents: FormData;
}

export type AddFileValidateRequest = Omit<AddFileSubmitRequest, 'documents'>;

/**
 * Legacy response types for POST user/home
 * @todo [CLENG-4993] remove when new_add_file_submission_enabled FF is decommissioned
 */
export interface AddFileSubmitResponseClient {
    email: Client['email'];
    type: Client['role'];
}

export interface AddFileSubmitResponseDocumentClient extends AddFileSubmitResponseClient {
    filenames: NonNullable<Document['name']>[];
}

export type AddFileSubmitResponse = ServerErrorResponse & {
    success: boolean;
    id: File['id'];
    ref_number: File['ref_number'];
    account?: Pick<Company, 'id' | 'name'>;
    request_bank_clients?: AddFileSubmitResponseClient[];
    request_id_verification_clients?: AddFileSubmitResponseClient[];
    send_wire_doc_clients?: AddFileSubmitResponseDocumentClient[];
    documents_clients?: AddFileSubmitResponseDocumentClient[];
    client_signed_documents?: Record<Client['email'], NonNullable<Document['name']>[]>;
};
